import { useTranslation } from "react-i18next";
import { QrCode } from "lucide-react";
import { FuellyLogo } from "@/components/FuellyBrand";
import { useTenant } from "@/tenant/TenantProvider";
import { scanErrorMessage } from "@/lib/serverMessages";
import { TABLE } from "@/constants/testIds";

/**
 * Shown instead of the order form when this restaurant requires a scan and there
 * is no live one behind this device.
 *
 * The case it exists for: a guest ordered here last week, the link is still in
 * their phone, and they open it from home. Before this they saw the ordering flow
 * and only found out at checkout. Now the first screen says what to do, because
 * standing at a table with a phone is the only thing that fixes it.
 *
 * Deliberately offers no way past. A "continue anyway" button would be a button
 * that produces a 401 — the server refuses the order either way.
 */
export default function ScanRequired({ reason }) {
  const { restaurant } = useTenant();
  const { t } = useTranslation();

  // `reason` is the server's refusal, if there was one. It is turned into the
  // guest's language by the situation it describes, never re-worded here.
  const refusal = scanErrorMessage(t, reason);

  return (
    <div
      data-testid={TABLE.scanRequired}
      className="min-h-screen px-8 flex flex-col items-center justify-center text-center"
    >
      <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mb-5">
        <QrCode size={28} className="text-neutral-500" />
      </div>

      <h1 className="text-2xl font-black tracking-tight text-neutral-900">{t("scan.title")}</h1>
      <p className="text-sm text-neutral-500 mt-2 max-w-[34ch] leading-relaxed">
        {refusal ||
          t("scan.body", { restaurant: restaurant?.name || t("scan.thisRestaurant") })}
      </p>

      <div className="mt-10 flex items-center gap-2 text-neutral-300">
        <FuellyLogo size={14} />
        <span className="text-[10px] font-mono uppercase tracking-[0.25em]">Fuelly</span>
      </div>
    </div>
  );
}
